import React, { useMemo } from 'react'
import { Box, Button, Icon, Link } from '@maui/elements'
import PropTypes from 'prop-types'
import CopyToClipboard from 'react-copy-to-clipboard'
import { HashText } from '../HashText'
import { formatMediumDate, titleize } from '@/utilities'
import { useIndicatorsTableContext } from './IndicatorsTableContext'
import { openModal } from '../../../events'
import {
  statusIcons,
  clickList,
  iconTypes,
  typeDisplayMap,
  severityColorByThreatScore,
  statusIconByThreatScore
} from './helpers'
import {
  getVirusTotalIndicatorLink,
  encodeURIComponentTwice
} from '../../../utilities'

const IndicatorLink = ({
  encodeValue = false,
  isThreatScore,
  type,
  value,
  ...props
}) => {
  // There are three cases for click events on these links.
  // 1. if the user is GTI regardless of threat score feature enablement, open normal link to VT
  // 2. if the user is not GTI and does not have threat score enabled, open the legacy modal
  // 3. if the user is not GTI and has threat score enabled, navigate to the indicator
  const { isGTI, navigate, virusTotalRoot } = useIndicatorsTableContext()

  const href = useMemo(() => {
    if (isGTI) {
      return getVirusTotalIndicatorLink(type, value, virusTotalRoot)
    }
    const pathValue = encodeValue ? encodeURIComponentTwice(value) : value
    return `/indicator/${type}/${pathValue}`
  }, [encodeValue, isGTI, type, value, virusTotalRoot])

  const onClick = useMemo(() => {
    if (isGTI) {
      return
    }

    return e => {
      e.preventDefault()

      if (isThreatScore) {
        window?.gtag('event', 'click', {
          event_category: 'Threat Score - Track',
          event_label: 'indicator_table_link_clicked'
        })
        navigate(href)
      } else {
        openModal('IndicatorModal', value, type)
      }
    }
  }, [
    isGTI,
    value,
    href,
    type,
    isThreatScore,
    navigate
  ])

  return (
    <Link
      {...props}
      textDecoration="none"
      _hover={{
        textDecoration: 'underline',
      }}
      href={href}
      onClick={onClick}
    />
  )
}

export const CustomCell = ({ column, render, row }) => {
  const {
    actorAssociations,
    associated_hashes: hashes,
    campaignAssociations,
    first_seen: firstSeen,
    is_exclusive: isExclusive,
    last_seen: lastSeen,
    malwareAssociations,
    mscore,
    threat_rating: threatRating = {},
    toolAssociations,
    type,
    value,
  } = row.original
  const { threat_score: threatScore } = threatRating || {}

  if (column.Header === 'Indicator Value') {
    return <IndicatorValue column={column} row={row} hashes={hashes} value={value} type={type} />
  } else if (column.Header === 'Type') {
    return typeDisplayMap[type]
  } else if (column.Header === 'Threat Score') {
    return <ThreatScore threatScore={threatScore} />
  } else if (column.Header === 'IC Score') {
    return <ICScore mscore={mscore} />
  } else if (column.Header === 'Associated Actors') {
    return <Associations associations={actorAssociations} />
  } else if (column.Header === 'Associated Malware') {
    return <Associations associations={malwareAssociations} />
  } else if (column.Header === 'Associated Tools') {
    return <Associations associations={toolAssociations} />
  } else if (column.Header === 'Associated Campaigns') {
    return <Associations associations={campaignAssociations} />
  } else if (column.id === 'is_exclusive') {
    return titleize(isExclusive?.toString()) || 'False'
  } else if (column.Header === 'First Seen') {
    return formatMediumDate(firstSeen)
  } else if (column.Header === 'Last Seen') {
    return formatMediumDate(lastSeen)
  }
  return render('Cell')
}

export const IndicatorValue = ({ column, row, hashes, type, value }) => {
  // NOTE we're special casing hash indicators with sha256 hashes as they're the preferred hash
  // in VT and we should probably mirror that behavior. If an indicator has a sha256 hash, we
  // assume it's a file type indicator and use it for all links in the hashes table cell.
  const sha256 = hashes?.find(hash => hash.type === 'sha256')?.value
  const indicator = sha256 ? { type: 'sha256', value: sha256 } : { type, value }

  return (
    <Box
      overflow='hidden'
      textOverflow='ellipsis'
    >
      {hashes
        ? hashes.map((hash, i) => {
          return (
            <Box
              key={i}
              display='grid'
              gridTemplateColumns='repeat(3, 1fr)'
            >
              <Box
                textStyle='caption'
                textTransform='uppercase'
                color='colorTextDeemphasis'
                whiteSpace='nowrap'
              >
                {hash.type}
              </Box>
              <IndicatorLink
                textDecoration='none'
                color='colorTextMain'
                fontWeight='400'
                pl='spacePaddingMedium'
                type={indicator.type}
                value={indicator.value}
                isThreatScore={column.threatScoreView}
              >
                <HashText value={hash.value} fontColor='colorActionPrimary' />
              </IndicatorLink>
              <CopyToClipboard text={hash.value}>
                <Button
                  py='0px'
                  variant='secondaryText'
                  ariaLabel={`Click to copy ${hash.value} to clipboard`}
                >
                  <Icon
                    name='copy-clipboard'
                    color='colorActionSecondary'
                    size="sm"
                  />
                </Button>
              </CopyToClipboard>
            </Box>
          )
        })
        : (
            <IndicatorLink
              type={type}
              value={value}
              isThreatScore={column.threatScoreView}
              encodeValue
            >
              {value}
            </IndicatorLink>
          )
        }
    </Box>
  )
}

export const ThreatScore = ({ threatScore }) => {
  return (
    <Box
      display='flex'
      alignItems='center'
      textStyle='bodySmall'
      my='0'
      color='colorTextMain'
    >
      {
        threatScore
          ? (
            <>
              <Icon
                height='textBodyLargeFontSize'
                width='textBodyLargeFontSize'
                mr='spaceGapSmall'
                color={severityColorByThreatScore(threatScore)}
                name={statusIconByThreatScore(threatScore)}
              />
              {threatScore}
            </>
            )
          : <Box color='colorTextDeemphasis'>---</Box>
      }
    </Box>
  )
}

export const ICScore = ({ mscore }) => {
  return (
    <Box
      display='flex'
      alignItems='center'
      textStyle='bodySmall'
      my='0'
      color='colorTextMain'
    >
      <Icon
        height='textBodyLargeFontSize'
        width='textBodyLargeFontSize'
        mr='spaceGapSmall'
        color={statusIcons(mscore)?.color}
        name={statusIcons(mscore)?.iconName}
      />
      {mscore}
    </Box>
  )
}

export const Associations = ({ associations }) => {
  return (
    <>
      {associations?.length === 0 && (
        <Box color='colorTextDeemphasis'>---</Box>
      )}
      {associations?.map((item, i) => {
        return (
          <Box
            key={i}
            display='flex'
            marginRight='spaceGapXLarge'
            alignItems='baseline'
            paddingY='2px'
          >
            <Box
              onClick={() => {
                const clickEvent = clickList[item.type.toLowerCase()]
                return clickEvent(item)
              }}
            >
              <Link
                display='flex'
                alignItems='center'
                gap='spaceGapMedium'
                color='colorActionPrimaryDefault'
              >
                <Icon size="sm" color='colorActionPrimaryDefault' name={iconTypes[item.type]} />
                <Box
                  textStyle='bodySmall'
                  color='colorActionPrimaryDefault'
                >
                  {item.name}
                </Box>
              </Link>
            </Box>
          </Box>
        )
      })}
    </>
  )
}

CustomCell.propTypes = {
  column: PropTypes.object.isRequired,
  render: PropTypes.func.isRequired,
  row: PropTypes.object.isRequired
}
